import { makeAutoObservable } from 'mobx';
import { IOsiServiceAbonentsButtonFeatureViewModel } from '@shared/types/mobx/features/osiAccruals';
import { AbonentOnServiceRequest, AbonentOnServiceResponse, OsiServiceResponse } from '@shared/types/osi/services';
import { injectable } from 'inversify';
import { notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import { getOsiServiceAbonents } from '@shared/api/osiServices/abonents/get';
import { GridSelectionModel } from '@mui/x-data-grid';
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { MutableRefObject } from 'react';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { axiosForReactQueryInstance } from '@shared/api/reactQuery';
import { ApiResponse } from '@shared/api/orval/models/apiResponse';

// Фаза 1: определяем группы по code вместо хардкода ID
const ADDITIONAL_GROUP_CODE = 'ADDITIONAL';
const PARKING_GROUP_CODE = 'PARKING';

// OSI-805: метод начисления «Тариф с одного проживающего». Определяем по code, а не по
// accuralMethodId — id методов генерируется в БД через IDENTITY и на тесте/бою различается,
// хардкод числа сломался бы при переносе на бой.
const TARIF_1LIVING_METHOD_CODE = 'TARIF_1LIVING';

@injectable()
export class OsiServiceAbonentsButtonFeatureViewModel implements IOsiServiceAbonentsButtonFeatureViewModel {
  isDialogOpen = false;

  isAbonentsTableLoading = false;

  isLoading = false;

  abonents: AbonentOnServiceResponse[] = [];

  // Force update confirmation dialog
  isForceUpdateConfirmDialogOpen = false;

  forceUpdateMessage: string | null = null;

  private _forceUpdatePayload: AbonentOnServiceRequest[] | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  private _selectedAbonentsIds: GridSelectionModel = [];

  get selectedAbonentsIds(): GridSelectionModel {
    return this._selectedAbonentsIds;
  }

  set selectedAbonentsIds(selectedAbonentsIds: GridSelectionModel) {
    this._selectedAbonentsIds = selectedAbonentsIds;
    // Фаза 2: parkingPlacesEdit убран — паркинг теперь отдельные абоненты
  }

  private _service: OsiServiceResponse | null = null;

  get service(): OsiServiceResponse | null {
    return this._service;
  }

  set service(service: OsiServiceResponse) {
    this._service = service;
  }

  get activeAbonentsCount(): number {
    return this._service?.countActiveAbonents ?? 0;
  }

  get abonentsCount(): number {
    return this._service?.countAllAbonents ?? 0;
  }

  get isAdditionalService(): boolean {
    return this._service?.serviceGroupCode === ADDITIONAL_GROUP_CODE;
  }

  get isParkingService(): boolean {
    return this._service?.serviceGroupCode === PARKING_GROUP_CODE;
  }

  // OSI-805: для услуг с тарифом «с одного проживающего» сумма начисления зависит не от площади,
  // а от количества проживающих — поэтому в окне выбора помещений председателю нужна колонка
  // «Проживают» вместо «Площадь» (иначе он не видит, по какому именно значению пойдёт расчёт).
  get isTarif1LivingService(): boolean {
    return this._service?.accuralMethodCode === TARIF_1LIVING_METHOD_CODE;
  }

  private _reloadCb: any = () => {};

  set reloadCb(reloadCb: any) {
    this._reloadCb = reloadCb;
  }

  private _apiRef: MutableRefObject<GridApiPro> | null = null;

  set apiRef(apiRef: MutableRefObject<GridApiPro>) {
    this._apiRef = apiRef;
  }

  parkingPlacesEdit = async () => {
    await this.setDefaultParkingPlaces();
  };

  setDefaultParkingPlaces = async () => {
    this.abonents
      .filter((abonent) => this.selectedAbonentsIds.includes(abonent.id ?? 0))
      .filter((abonent) => !abonent.parkingPlaces)
      .forEach(async (abonent): Promise<void> => {
        await this._apiRef?.current?.startCellEditMode({ id: abonent.id ?? 0, field: 'parkingPlaces' });
        await this._apiRef?.current?.setEditCellValue({ id: abonent.id ?? 0, field: 'parkingPlaces', value: 1 });
        await this._apiRef?.current?.stopCellEditMode({ id: abonent.id ?? 0, field: 'parkingPlaces' });
      });
  };

  selectByType = (type: AreaTypeCodes) => {
    const typedAbonents = this.abonents.filter((abonent) => abonent.areaTypeCode === type);
    this.selectedAbonentsIds = [
      ...this._selectedAbonentsIds,
      ...typedAbonents.map((abonent) => abonent.id ?? 0)
    ].filter((value, index, array) => array.indexOf(value) === index);
  };

  unselectByType = (type: AreaTypeCodes) => {
    const typedAbonentsIds = this.abonents
      .filter((abonent) => abonent.areaTypeCode === type)
      .map((abonent) => abonent.id ?? 0);

    this.selectedAbonentsIds = this._selectedAbonentsIds.filter((id) => !typedAbonentsIds.includes(id as number));
  };

  selectorStateByType = (type: AreaTypeCodes): { checked: boolean; indeterminate: boolean; disabled: boolean } => {
    const typedAbonents = this.abonents.filter((abonent) => abonent.areaTypeCode === type);
    const isAllSelected = typedAbonents.every((abonent) => this._selectedAbonentsIds.includes(abonent.id ?? 0));
    const isAllNotSelected = typedAbonents.every((abonent) => !this._selectedAbonentsIds.includes(abonent.id ?? 0));

    const status = {
      checked: typedAbonents.length ? isAllSelected : false,
      indeterminate: !isAllSelected && !isAllNotSelected,
      disabled: !typedAbonents.length
    };

    return status;
  };

  // OSI-795: помещения — все абоненты, кроме арендаторов (ARENDATOR).
  // Header-чекбокс «выбрать всё» должен управлять ТОЛЬКО этим набором.
  private get _premisesAbonents(): AbonentOnServiceResponse[] {
    return this.abonents.filter((abonent) => abonent.areaTypeCode !== AreaTypeCodes.ARENDATOR);
  }

  // Состояние header-чекбокса помещений: считается ТОЛЬКО по помещениям (без арендаторов).
  // Аналог selectorStateByType, но по набору «все типы кроме ARENDATOR».
  get premisesSelectorState(): { checked: boolean; indeterminate: boolean; disabled: boolean } {
    const premises = this._premisesAbonents;
    const isAllSelected = premises.every((abonent) => this._selectedAbonentsIds.includes(abonent.id ?? 0));
    const isAllNotSelected = premises.every((abonent) => !this._selectedAbonentsIds.includes(abonent.id ?? 0));

    return {
      checked: premises.length ? isAllSelected : false,
      indeterminate: !isAllSelected && !isAllNotSelected,
      disabled: !premises.length
    };
  }

  // Выбрать все помещения (по образцу selectByType): добавляем id всех помещений к уже выбранным
  // с dedup, не трогая выбранных вручную арендаторов.
  selectAllPremises = () => {
    this.selectedAbonentsIds = [
      ...this._selectedAbonentsIds,
      ...this._premisesAbonents.map((abonent) => abonent.id ?? 0)
    ].filter((value, index, array) => array.indexOf(value) === index);
  };

  // Снять выбор со всех помещений (по образцу unselectByType): убираем только id помещений,
  // оставляя выбранных вручную арендаторов.
  unselectAllPremises = () => {
    const premisesIds = this._premisesAbonents.map((abonent) => abonent.id ?? 0);
    this.selectedAbonentsIds = this._selectedAbonentsIds.filter((id) => !premisesIds.includes(id as number));
  };

  openAbonentsTable = async (): Promise<void> => {
    try {
      this.isAbonentsTableLoading = true;
      if (!this._service?.id) throw new Error('service id is undefined');

      this.abonents = await getOsiServiceAbonents(this._service.id);

      this.initialSelectAbonents();

      this.isAbonentsTableLoading = false;

      this.isDialogOpen = true;
    } catch (e) {
      logger.error(e);
      notifications.error();
    }
  };

  initialSelectAbonents = () => {
    if (!this.abonents.length) return;

    const selectedIds = this.abonents.filter((abonent) => abonent.checked).map((abonent) => abonent.id) ?? [];

    this._selectedAbonentsIds = selectedIds as GridSelectionModel;
  };

  private saveAbonents = async (serviceId: number, payload: AbonentOnServiceRequest[], forceUpdate = false): Promise<ApiResponse> => {
    return axiosForReactQueryInstance<ApiResponse>(
      {
        url: `/api/OsiServices/${serviceId}/abonents`,
        method: 'PUT',
        data: payload,
        params: forceUpdate ? { forceUpdate: true } : undefined
      }
    );
  };

  save = async (): Promise<void> => {
    try {
      if (!this._service?.id) throw new Error('service id is undefined');

      this.isLoading = true;

      // Фаза 2: parkingPlaces убран — паркинг теперь отдельные абоненты
      const checkedAbonents = this.abonents
        .filter((abonent) => this.selectedAbonentsIds.includes(abonent.id ?? 0))
        .map((abonent) => ({
          abonentId: Number(abonent.id),
          checked: true
        } as AbonentOnServiceRequest));
      const unCheckedAbonents = this.abonents
        .filter((abonent) => abonent.checked)
        .filter((abonent) => !this.selectedAbonentsIds.includes(abonent.id ?? 0))
        .map((abonent) => ({
          abonentId: Number(abonent.id),
          checked: false
        } as AbonentOnServiceRequest));

      const payload: AbonentOnServiceRequest[] = [...checkedAbonents, ...unCheckedAbonents];

      const response = await this.saveAbonents(this._service.id, payload);

      // If code is 100, show confirmation dialog
      if (response && response.code === 100) {
        this._forceUpdatePayload = payload;
        this.forceUpdateMessage = response.message ?? 'Требуется подтверждение для обновления';
        this.isForceUpdateConfirmDialogOpen = true;
        return;
      }

      if (response && response.code !== 0) {
        notifications.error(response.message ?? '');
        return;
      }

      await this._reloadCb?.();
      this.isDialogOpen = false;
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  closeForceUpdateConfirmDialog = (): void => {
    this.isForceUpdateConfirmDialogOpen = false;
    this._forceUpdatePayload = null;
    this.forceUpdateMessage = null;
  };

  confirmForceUpdate = async (): Promise<void> => {
    if (!this._forceUpdatePayload || !this._service?.id) return;

    this.isLoading = true;

    try {
      const response = await this.saveAbonents(this._service.id, this._forceUpdatePayload, true);

      if (response && response.code !== 0) {
        notifications.error(response.message ?? '');
        return;
      }

      await this._reloadCb?.();
      this.isDialogOpen = false;
      this.closeForceUpdateConfirmDialog();
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  cancelEditing = async (): Promise<void> => {
    this.isDialogOpen = false;
    this.closeForceUpdateConfirmDialog();
    await this._reloadCb?.();
  };

  changeParkingPlaces = (id: number, parkingPlaces: number | string): void => {
    const abonent = this.abonents.find((abonent) => abonent.id === id);
    if (!abonent || !this._apiRef?.current) return;

    abonent.parkingPlaces = Number(parkingPlaces);
  };
}
